import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollText, ChevronLeft, ChevronRight, RefreshCw, Filter, X } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { Redirect } from "wouter";
import { getQueryFn } from "@/lib/queryClient";
import { format } from "date-fns";
import { PageHeader } from "@/components/page-header";
import { usePageTitle } from "@/hooks/use-page-title";

interface AuditLog {
  id: string;
  occurredAt: string;
  correlationId: string | null;
  actorType: string;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  result: string;
  entityType: string | null;
  entityId: string | null;
  locationCode: string | null;
  details: Record<string, unknown> | null;
}

interface LogsResponse {
  logs: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ActionsResponse {
  actions: string[];
}

interface UsersResponse {
  users: { id: string; email: string; username: string }[];
}

export default function Logs() {
  usePageTitle("Logs");
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [action, setAction] = useState<string>("");
  const [actorUserId, setActorUserId] = useState<string>("");
  const [result, setResult] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);

  if (!user?.isSuperAdmin) {
    return <Redirect to="/dashboard" />;
  }

  const queryParams = new URLSearchParams();
  queryParams.set("page", page.toString());
  queryParams.set("limit", limit.toString());
  if (startDate) queryParams.set("startDate", startDate);
  if (endDate) queryParams.set("endDate", endDate);
  if (action) queryParams.set("action", action);
  if (actorUserId) queryParams.set("actorUserId", actorUserId);
  if (result) queryParams.set("result", result);

  const logsUrl = `/api/admin/logs?${queryParams.toString()}`;

  const { data: logsData, isLoading, refetch, isFetching } = useQuery<LogsResponse>({
    queryKey: ["/api/admin/logs", page, limit, startDate, endDate, action, actorUserId, result],
    queryFn: async () => {
      const response = await fetch(logsUrl, { credentials: "include" });
      if (!response.ok) {
        if (response.status === 401) return null;
        throw new Error("Failed to fetch logs");
      }
      return response.json();
    },
  });

  const { data: actionsData } = useQuery<ActionsResponse>({
    queryKey: ["/api/admin/logs/actions"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const { data: usersData } = useQuery<UsersResponse>({
    queryKey: ["/api/admin/logs/users"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setAction("");
    setActorUserId("");
    setResult("");
    setPage(1);
  };

  const hasActiveFilters = startDate || endDate || action || actorUserId || result;

  const formatActionName = (actionName: string) => {
    return actionName.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  return (
    <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={[{ label: "Administration" }, { label: "Audit Logs" }]}
        title={
          <div className="flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-primary" />
            <span>Audit Logs</span>
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="button-toggle-filters"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-2">
                  Active
                </Badge>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              data-testid="button-refresh-logs"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        }
      />

      {showFilters && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Filters</CardTitle>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  data-testid="button-clear-filters"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  data-testid="input-end-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="action">Action</Label>
                <Select value={action} onValueChange={(value) => { setAction(value === "all" ? "" : value); setPage(1); }}>
                  <SelectTrigger id="action" data-testid="select-action">
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    {actionsData?.actions.map((a) => (
                      <SelectItem key={a} value={a}>
                        {formatActionName(a)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="actor">User</Label>
                <Select value={actorUserId} onValueChange={(value) => { setActorUserId(value === "all" ? "" : value); setPage(1); }}>
                  <SelectTrigger id="actor" data-testid="select-actor">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {usersData?.users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="result">Result</Label>
                <Select value={result} onValueChange={(value) => { setResult(value === "all" ? "" : value); setPage(1); }}>
                  <SelectTrigger id="result" data-testid="select-result">
                    <SelectValue placeholder="All results" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All results</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failure">Failure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="table-zebra">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Location</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6} className="h-12">
                        <div className="h-4 bg-muted animate-pulse rounded w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : logsData?.logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logsData?.logs.map((log) => (
                    <TableRow key={log.id} data-testid={`row-log-${log.id}`}>
                      <TableCell className="font-mono text-xs">
                        {format(new Date(log.occurredAt), "yyyy-MM-dd HH:mm:ss")}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{formatActionName(log.action)}</span>
                      </TableCell>
                      <TableCell>
                        {log.actorType === "system" ? (
                          <Badge variant="outline">System</Badge>
                        ) : (
                          <span className="text-sm">{log.actorEmail || "Unknown"}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={log.result === "success" ? "default" : "destructive"}>
                          {log.result}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.entityType ? (
                          <span>
                            {log.entityType}
                            {log.entityId && <span className="text-muted-foreground"> #{log.entityId.slice(0, 8)}</span>}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.locationCode || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {logsData && logsData.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, logsData.pagination.total)} of {logsData.pagination.total} logs
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="button-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm">
                  Page {page} of {logsData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(logsData.pagination.totalPages, p + 1))}
                  disabled={page >= logsData.pagination.totalPages}
                  data-testid="button-next-page"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
